import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex items-center overflow-hidden bg-hero-gradient"
      style={{ fontFamily: '"Space Grotesk", sans-serif' }}
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/a19b9c4864c56cebe054eb111970f0c7.jpg"
          alt="Professional moving crew loading truck in Minneapolis"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/80 to-navy/30"></div>
      </div>

      {/* Decorative lines */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 right-0 w-96 h-96 border border-teal/10 rounded-full translate-x-1/2"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 border border-teal/10 rounded-full translate-y-1/2"></div>
        <div className="absolute top-1/2 left-1/3 w-2 h-2 bg-teal/40 rounded-full"></div>
        <div className="absolute top-1/3 right-1/3 w-1.5 h-1.5 bg-teal/30 rounded-full"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32 w-full">
        <div className="max-w-3xl">
          {/* Tag */}
          <div
            className={`inline-flex items-center gap-2 bg-teal/20 border border-teal/30 rounded-full px-4 py-2 mb-8 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="w-1.5 h-1.5 bg-teal rounded-full"></div>
            <span className="text-teal text-xs font-body font-medium tracking-widest uppercase">
              Minneapolis&apos;s Most Trusted Moving Company
            </span>
          </div>

          {/* H1 */}
          <h1
            className={`font-heading font-800 text-white leading-tight mb-6 transition-all duration-700 delay-100 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ fontSize: 'clamp(2.8rem, 5vw, 4.5rem)', lineHeight: '1.08' }}
          >
            Move with{' '}
            <span className="text-teal">Confidence.</span>
            <br />
            Arrive with{' '}
            <span className="relative">
              Peace of Mind.
              <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-teal/40"></span>
            </span>
          </h1>

          {/* Subheadline */}
          <p
            className={`text-white/70 text-lg font-body leading-relaxed mb-10 max-w-xl transition-all duration-700 delay-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            Licensed, insured, and family-owned — TrueCare Moving Services delivers affordable residential and commercial moving across Minneapolis and the Twin Cities with care you can trust.
          </p>

          {/* CTAs */}
          <div
            className={`flex flex-col sm:flex-row gap-4 mb-14 transition-all duration-700 delay-300 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <Link
              to="/#quote"
              className="inline-flex items-center justify-center gap-2 bg-teal hover:bg-teal-dark text-navy font-heading font-700 text-base px-8 py-4 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer animate-pulse-teal"
            >
              <i className="ri-file-text-line text-lg"></i>
              Get Free Quote
            </Link>
            <a
              href="tel:+16123674481"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-heading font-600 text-base px-8 py-4 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer backdrop-blur-sm"
            >
              <i className="ri-phone-fill text-teal text-lg"></i>
              Call (612) 367-4481
            </a>
          </div>

          {/* Trust Badges */}
          <div
            className={`flex flex-wrap gap-x-6 gap-y-3 transition-all duration-700 delay-400 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            {[
              { icon: 'ri-shield-check-line', text: 'Licensed & Insured' },
              { icon: 'ri-award-line', text: 'A+ BBB Rated' },
              { icon: 'ri-star-fill', text: '5-Star Rated' },
              { icon: 'ri-group-line', text: 'Family-Owned' },
            ].map((badge) => (
              <div key={badge.text} className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${badge.icon} text-teal text-sm`}></i>
                </div>
                <span className="text-white/60 text-sm font-body">{badge.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Floating review card */}
        <div
          className={`hidden lg:flex absolute bottom-16 right-8 items-center gap-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 max-w-xs transition-all duration-700 delay-500 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="w-12 h-12 flex items-center justify-center bg-teal/20 rounded-xl shrink-0">
            <i className="ri-star-fill text-teal text-xl"></i>
          </div>
          <div>
            <div className="flex gap-0.5 mb-1">
              {[1, 2, 3, 4, 5].map((s) => (
                <i key={s} className="ri-star-fill text-teal text-xs"></i>
              ))}
            </div>
            <div className="text-white font-heading font-700 text-sm">231 Google Reviews</div>
            <div className="text-white/50 font-body text-xs">5.0 Average Rating</div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 opacity-50">
        <span className="text-white/50 text-xs font-body tracking-widest uppercase">Scroll</span>
        <div className="w-0.5 h-8 bg-gradient-to-b from-white/50 to-transparent"></div>
      </div>
    </section>
  );
};

export default HeroSection;
